import { ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, PressableProps, StyleSheet, TextStyle } from 'react-native';
import CarouselLib, { ICarouselInstance, TCarouselProps } from 'react-native-reanimated-carousel';
import { DeviceType } from 'expo-device';
import { useBoolean } from 'usehooks-ts';
import { useOnKeyDown } from '@pletly/common';

import { useDeviceType } from '../../hooks';
import { Text } from '../Text';
import { colors, spacing } from '../../theme';
import { PaginationControls } from './PaginationControls';
import { PaginationDots } from './PaginationDots';

export type CarouselProps<Item> = Omit<TCarouselProps<Item>, 'renderItem'> &
	Pick<PressableProps, 'style' | 'onLayout' | 'onLongPress'> & {
		renderItem: (props: { item: Item; index: number; activeIndex: number }) => ReactElement;
		onPress?: (activeIndex: number) => void;
		indexMarkStyle?: TextStyle;
		withKeyboardNavigation?: boolean;
	};
const DEFAULT_HEIGHT = 350;
export function Carousel<Item>({
	style,
	onLayout,
	onPress,
	onLongPress,
	data,
	height = DEFAULT_HEIGHT,
	renderItem,
	indexMarkStyle,
	withKeyboardNavigation = false,
	...carouselProps
}: CarouselProps<Item>) {
	const deviceType = useDeviceType();
	const { setTrue: setHovered, setFalse: setHoverOut, value: isHovered } = useBoolean(false);
	const carouselRef = useRef<ICarouselInstance | null>(null);
	const [activeIndex, setActiveIndex] = useState(0);

	const onNextPress = useCallback(() => carouselRef.current?.next(), []);
	const onPreviousPress = useCallback(() => carouselRef.current?.prev(), []);

	const keyboardNavigation = useMemo(() => {
		if (!withKeyboardNavigation) return undefined;

		return {
			ArrowRight: onNextPress,
			ArrowLeft: onPreviousPress,
		};
	}, [withKeyboardNavigation, onNextPress, onPreviousPress]);

	useOnKeyDown(keyboardNavigation);

	return (
		<Pressable
			style={style}
			onLayout={onLayout}
			onPress={() => onPress?.(activeIndex)}
			onHoverIn={setHovered}
			onHoverOut={setHoverOut}
			onLongPress={onLongPress}
		>
			<CarouselLib
				enabled={deviceType === DeviceType.PHONE}
				ref={carouselRef}
				loop={Platform.OS === 'web' && deviceType === DeviceType.DESKTOP}
				data={data}
				windowSize={2}
				onConfigurePanGesture={(gestureChain) => {
					gestureChain.config.touchAction = 'pan-y';
					gestureChain.activeOffsetX([-20, 20]);
				}}
				pagingEnabled
				onProgressChange={(_, absoluteProgress) => {
					const index = Math.round(absoluteProgress);
					if (activeIndex !== index) {
						setActiveIndex(index);
					}
				}}
				height={height}
				renderItem={({ item, index }) => renderItem({ item, index, activeIndex })}
				{...carouselProps}
			/>
			{data.length > 1 && (
				<>
					<Text
						style={[styles.imageIndexMark, indexMarkStyle]}
						appearance="alternative"
						category="c1"
					>{`${activeIndex + 1}/${data.length}`}</Text>

					{Platform.OS === 'web' && isHovered && (
						<PaginationControls
							currentIndex={activeIndex}
							itemsCount={data.length}
							onNextPress={onNextPress}
							onPreviousPress={onPreviousPress}
						/>
					)}

					<PaginationDots currentIndex={activeIndex} length={data.length} />
				</>
			)}
		</Pressable>
	);
}

const styles = StyleSheet.create({
	imageIndexMark: {
		backgroundColor: colors.primarySemiTransparent,
		position: 'absolute',
		top: spacing(),
		right: spacing(),
		paddingHorizontal: spacing(),
		paddingVertical: 4,
		borderRadius: 10,
		overflow: 'hidden',
	},
});
